import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useCurrentUser } from '../context/UserContext';
import ZbSummary, { fmtDate } from '../components/ZbSummary';
import RowBreakdown from '../components/RowBreakdown';
import { describeAcceptResult } from '../utils/deliveryNotices';
import {
  canCancelUpload, isCancelled, cancelledSummary, describeCancelError, describeCancelResult,
} from '../utils/deliveryCancel';
import DeliveryLeadsPanel from '../components/DeliveryLeadsPanel';
import ProgramCarousel, { PROGRAM_STATUS_COLORS } from '../components/ProgramCarousel';
import Section from '../components/Section';
import TaskList from '../components/TaskList';
import useSort from '../hooks/useSort';
import { useTaskActions } from '../hooks/useTaskActions';
import { LEAD_STATUS_COLORS } from '../utils/leadStatus';
import { supplierGoalsToRows } from '../utils/statusBreakdown';
import CollapsibleSection from '../components/CollapsibleSection';
import RequiredFieldsSummary from '../components/RequiredFieldsSummary';
import { buildStatusGroupMap, splitByStatusGroup, buildProgramOptionGroups } from '../utils/statusGroups';
import SearchableSelect from '../components/SearchableSelect';

const API = process.env.REACT_APP_API_URL;

const STATUS_STYLES = {
  'Pending':         { bg: '#fef9e7', color: '#b7770d', border: '#f39c12' },
  'Delivered to PM': { bg: '#eafaf1', color: '#1e8449', border: '#27ae60' },
  'Cancelled':       { bg: '#f4f5f6', color: '#7f8c8d', border: '#c8ccd0' },
};

function statusBadge(status) {
  const s = STATUS_STYLES[status] || { bg: '#f5f5f5', color: '#555', border: '#ccc' };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

// Shared style helpers
const thBlue = { padding: '7px 12px', textAlign: 'left', borderBottom: '1px solid #aed6f1', fontWeight: '600', color: '#1a6da8', fontSize: '12px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
const tdMono = { padding: '6px 12px', borderBottom: '1px solid #eaf2fb', fontSize: '12px', fontFamily: 'monospace', color: '#333' };
const tdText = { ...tdMono, fontFamily: 'sans-serif', color: '#555' };

// ---------------------------------------------------------------------------
// Delivery card
// ---------------------------------------------------------------------------
function DeliveryCard({ d, onDownload, onDelete, deleting, onAccepted }) {
  const id = d.id;
  const { currentUser } = useCurrentUser();
  const canAccept = ['admin', 'program_manager', 'supporting_program_manager'].includes(currentUser?.role);
  const [showLeads, setShowLeads] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptMsg, setAcceptMsg] = useState(null);
  // Bumped after a successful accept so DeliveryLeadsPanel (keyed on it) remounts
  // and refetches instead of showing whatever it loaded before the accept — it
  // otherwise only fetches once on mount and has no other reason to refresh.
  const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptMsg(null);
    try {
      const res = await axios.post(`${API}/supplier-deliveries/${id}/accept`);
      const outcome = describeAcceptResult(res.data);
      setAcceptMsg(outcome.warn ? outcome.text : `✓ ${outcome.text}`);
      setLeadsRefreshKey(k => k + 1);
      // Card's own row_counts/status came from the parent's last list fetch and
      // won't reflect this change until that list is reloaded.
      if (onAccepted) onAccepted();
    } catch (err) {
      setAcceptMsg('Accept failed: ' + (err.response?.data?.detail || err.message));
    }
    setAccepting(false);
  };

  const handleDownloadBatchFeedback = () => {
    axios.get(`${API}/supplier-deliveries/${id}/export-batch-feedback`, { responseType: 'blob' })
      .then(res => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = `${(d.filename || `delivery_${id}`).replace(/\.[^.]+$/, '')}_batch_feedback.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Download failed'));
  };

  // Supplier changed their mind after choosing "Yes" on a batch — release it
  // back to the normal PM queue. Purely informational on the backend, see
  // hold-for-resubmission endpoint.
  const [releasing, setReleasing] = useState(false);
  const handleRelease = async () => {
    setReleasing(true);
    try {
      await axios.patch(`${API}/supplier-deliveries/${id}/hold-for-resubmission`, { held: false });
      if (onAccepted) onAccepted();
    } catch {
      alert('Could not release this batch — please try again.');
    }
    setReleasing(false);
  };

  // Cancel a bad upload before a PM accepts it. Inline confirm (with an optional reason the PM sees)
  // instead of a bare window.confirm, since this removes the batch's leads and can't be undone.
  const cancelled = isCancelled(d);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelMsg, setCancelMsg] = useState(null);
  const handleCancel = async () => {
    setCancelling(true);
    setCancelMsg(null);
    try {
      const res = await axios.post(`${API}/supplier-deliveries/${id}/cancel`, { reason: cancelReason.trim() || null });
      setConfirmingCancel(false);
      setCancelMsg({ ok: true, text: describeCancelResult(res.data) });
      if (onAccepted) onAccepted();
    } catch (err) {
      setCancelMsg({ ok: false, text: describeCancelError(err) });
    }
    setCancelling(false);
  };

  return (
    <div style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', overflow: 'hidden', opacity: cancelled ? 0.7 : 1 }}>
      <div style={{ padding: '14px 16px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50', marginBottom: '3px' }}>{d.program_name || '—'}</div>
          {(d.supplier_name || d.uploaded_by_name) && (
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
              {d.supplier_name && <span>{d.supplier_name}</span>}
              {d.supplier_name && d.uploaded_by_name && <span style={{ margin: '0 5px', color: '#ccc' }}>·</span>}
              {d.uploaded_by_name && <span>by {d.uploaded_by_name}</span>}
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ color: '#aaa' }}>📄</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename || '(unnamed file)'}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '7px', flexShrink: 0 }}>
          <div style={{ fontSize: '11px', color: '#aaa' }}>{fmtDate(d.upload_date)}</div>
          {statusBadge(d.status)}
          {d.held_for_resubmission && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ background: '#fff3e0', color: '#b7770d', border: '1px solid #f0c896', borderRadius: '4px', padding: '1px 8px', fontSize: '10px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                ⏳ Held for resubmission
              </span>
              {/* A held delivery can't be accepted (the server refuses), so a PM/admin needs the release link too. */}
              <button onClick={handleRelease} disabled={releasing}
                style={{ padding: '0', border: 'none', background: 'none', color: '#1a6da8', cursor: releasing ? 'not-allowed' : 'pointer', fontSize: '10px', textDecoration: 'underline' }}>
                {releasing ? '…' : (canAccept ? 'Release hold' : 'Release to PM')}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', marginTop: '2px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {d.zb_results ? (
              <>
                <button onClick={handleDownloadBatchFeedback}
                  style={{ padding: '4px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  ⬇ Batch Feedback
                </button>
                <button onClick={() => onDownload(id, d.filename, true)}
                  style={{ padding: '4px 10px', background: 'white', border: '1px solid #3498db', color: '#3498db', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  ⬇ Original
                </button>
              </>
            ) : (
              <button onClick={() => onDownload(id, d.filename, true)}
                style={{ padding: '4px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                ⬇ Download
              </button>
            )}
            {canCancelUpload(d, currentUser?.role) && !confirmingCancel && (
              <button onClick={() => { setConfirmingCancel(true); setCancelMsg(null); }}
                title="Withdraw this file before a PM accepts it"
                style={{ padding: '4px 10px', background: 'white', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                Cancel upload
              </button>
            )}
            {onDelete && (
              <button onClick={() => onDelete(id)} disabled={deleting?.[id]}
                style={{ padding: '4px 10px', background: deleting?.[id] ? '#ccc' : '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: deleting?.[id] ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                {deleting?.[id] ? '…' : '🗑'}
              </button>
            )}
          </div>
          {d.zb_results && !cancelled && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '2px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLeads(o => !o)}
                style={{ padding: '4px 10px', background: 'white', border: '1px solid #7f8c8d', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                {showLeads ? '▾' : '▸'} View Leads
              </button>
              {canAccept && (
                <button onClick={handleAccept} disabled={accepting || d.held_for_resubmission}
                  title={d.held_for_resubmission ? 'The supplier is holding this delivery. Release the hold first.' : undefined}
                  style={{ padding: '4px 10px', background: (accepting || d.held_for_resubmission) ? '#ccc' : '#1a6da8', color: 'white', border: 'none', borderRadius: '4px', cursor: (accepting || d.held_for_resubmission) ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                  {accepting ? '…' : '✓ Accept Delivery'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {acceptMsg && (
        <div style={{ padding: '6px 16px', fontSize: '12px', color: acceptMsg.startsWith('✓') ? '#1e8449' : '#c0392b', background: '#fafbfc', borderTop: '1px solid #f0f0f0' }}>
          {acceptMsg}
        </div>
      )}
      {cancelled && (
        <div style={{ padding: '8px 16px', fontSize: '12px', color: '#7f8c8d', background: '#fafbfc', borderTop: '1px solid #f0f0f0' }}>
          {cancelledSummary(d)}. Its leads were removed, so nothing from this file is waiting for review.
        </div>
      )}
      {confirmingCancel && !cancelled && (
        <div style={{ padding: '10px 16px', fontSize: '12px', background: '#fdf2f0', borderTop: '1px solid #f5c6c0' }}>
          <div style={{ color: '#a93226', fontWeight: '600', marginBottom: '6px' }}>
            Cancel this upload? Its leads are removed and the PM is told it was withdrawn. You can upload a corrected file afterwards.
          </div>
          <input value={cancelReason} onChange={e => setCancelReason(e.target.value)} maxLength={500}
            placeholder="Reason (optional), e.g. wrong file"
            style={{ width: '100%', maxWidth: '420px', padding: '5px 8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button onClick={handleCancel} disabled={cancelling}
              style={{ padding: '4px 12px', background: cancelling ? '#ccc' : '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: cancelling ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
              {cancelling ? 'Cancelling…' : 'Yes, cancel this upload'}
            </button>
            <button onClick={() => { setConfirmingCancel(false); setCancelReason(''); setCancelMsg(null); }} disabled={cancelling}
              style={{ padding: '4px 12px', background: 'white', border: '1px solid #ccc', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              Keep it
            </button>
          </div>
        </div>
      )}
      {cancelMsg && (
        <div style={{ padding: '6px 16px', fontSize: '12px', color: cancelMsg.ok ? '#1e8449' : '#c0392b', background: '#fafbfc', borderTop: '1px solid #f0f0f0' }}>
          {cancelMsg.text}
        </div>
      )}
      {(d.row_counts || d.zb_results) && !cancelled && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 16px', background: '#fafbfc' }}>
          {d.row_counts && <RowBreakdown counts={d.row_counts} status={d.status} showReason={canAccept} />}
          {d.zb_results && <ZbSummary zbResults={d.zb_results} checkedAt={d.zb_checked_at} />}
        </div>
      )}
      {showLeads && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 16px' }}>
          <DeliveryLeadsPanel key={leadsRefreshKey} id={id} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Valid IDs table (Step 2) — sortable
// ---------------------------------------------------------------------------
function ValidIdsTable({ goalsByComponent, selectedProgramId }) {
  const rows = goalsByComponent.flatMap(({ comp, goals: gList }) =>
    gList.map(g => ({
      program_id: selectedProgramId,
      component_id: comp.id,
      goal_id: g.id,
      component_name: comp.component_name,
      goal_name: g.goal_name || '(unnamed)',
    }))
  );
  const { sorted, toggle, ind } = useSort(rows, 'component_name');
  const cols = [
    { key: 'program_id', label: 'Program ID' },
    { key: 'component_id', label: 'Component ID' },
    { key: 'goal_id', label: 'Goal ID' },
    { key: 'component_name', label: 'Component Name' },
    { key: 'goal_name', label: 'Goal Name' },
  ];
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', background: 'white', borderRadius: '6px', overflow: 'hidden', border: '1px solid #aed6f1' }}>
      <thead><tr>{cols.map(c => <th key={c.key} style={thBlue} onClick={() => toggle(c.key)}>{c.label}{ind(c.key)}</th>)}</tr></thead>
      <tbody>
        {sorted.map(r => (
          <tr key={r.goal_id} style={{ borderBottom: '1px solid #eaf2fb' }}>
            <td style={tdMono}>{r.program_id}</td>
            <td style={tdMono}>{r.component_id}</td>
            <td style={tdMono}>{r.goal_id}</td>
            <td style={tdText}>{r.component_name}</td>
            <td style={tdText}>{r.goal_name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Drag-and-drop upload zone with email column detection
// ---------------------------------------------------------------------------
function DropZone({ uploadFile, setUploadFile, setUploadResult, fileRef }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    setUploadFile(file);
    setUploadResult(null);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
      onClick={() => fileRef.current?.click()}
      style={{
        border: `2px dashed ${isDragging ? '#3498db' : uploadFile ? '#27ae60' : '#ccc'}`,
        borderRadius: '8px', padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
        background: isDragging ? '#eaf4fb' : uploadFile ? '#eafaf1' : '#fafafa',
        transition: 'border-color 0.15s, background 0.15s', userSelect: 'none',
      }}
    >
      <div style={{ fontSize: '26px', marginBottom: '8px' }}>
        {isDragging ? '📂' : uploadFile ? '✅' : '📁'}
      </div>
      <div style={{ fontSize: '13px', fontWeight: uploadFile ? '600' : '400', color: uploadFile ? '#1e8449' : isDragging ? '#1a6da8' : '#555' }}>
        {uploadFile ? uploadFile.name : isDragging ? 'Drop to upload' : 'Drop file here, or click to browse'}
      </div>
      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
        {uploadFile
          ? <>{(uploadFile.size / 1024).toFixed(1)} KB · <span onClick={e => { e.stopPropagation(); setUploadFile(null); setUploadResult(null); }} style={{ color: '#e74c3c', cursor: 'pointer', textDecoration: 'underline' }}>Remove</span></>
          : 'CSV or XLSX · All rows will be validated'}
      </div>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])} />
    </div>
  );
}

// Simple always-visible ZeroBounce toggle — no async scan needed
function ZbToggle({ enabled, onToggle, emailCol, onColChange }) {
  return (
    <div style={{ marginTop: '10px', padding: '10px 14px', background: '#f8f9fa', border: '1px solid #e0e4e8', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#555', userSelect: 'none' }}>
        <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)}
          style={{ cursor: 'pointer', width: '14px', height: '14px' }} />
        📧 ZeroBounce email verification
      </label>
      {enabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <span style={{ color: '#777' }}>Column name:</span>
          <input
            value={emailCol}
            onChange={e => onColChange(e.target.value)}
            style={{ padding: '3px 8px', border: '1px solid #aed6f1', borderRadius: '4px', fontSize: '12px', width: '140px', background: 'white' }}
            placeholder="email"
          />
          <span style={{ color: '#aaa', fontSize: '11px' }}>(case-insensitive)</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation error panel
// ---------------------------------------------------------------------------
function ValidationError({ error }) {
  if (!error) return null;
  const message = typeof error === 'object' ? error.message : error;
  const errors = typeof error === 'object' ? error.errors : null;
  const validValues = typeof error === 'object' ? error.valid_values : null;
  return (
    <div style={{ padding: '14px', borderRadius: '6px', background: '#fdedec', border: '1px solid #e74c3c' }}>
      <div style={{ color: '#c0392b', fontWeight: '600', fontSize: '13px', marginBottom: errors ? '10px' : 0 }}>❌ {message}</div>
      {errors?.length > 0 && (
        <div style={{ marginBottom: validValues ? '12px' : 0 }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#922b21', marginBottom: '4px' }}>Row errors:</div>
          <div style={{ background: '#fff5f5', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '8px', fontSize: '11px', color: '#922b21', maxHeight: '140px', overflowY: 'auto' }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        </div>
      )}
      {validValues?.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>Valid IDs for this program:</div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px', background: 'white', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd' }}>
            <thead><tr style={{ background: '#f5f5f5' }}>{['Program ID','Component ID','Goal ID','Component Name','Goal Name'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#555', fontWeight: '600' }}>{h}</th>)}</tr></thead>
            <tbody>{validValues.map(v => <tr key={v.goal_id} style={{ borderBottom: '1px solid #f5f5f5' }}><td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{v.program_id}</td><td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{v.component_id}</td><td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{v.goal_id}</td><td style={{ padding: '4px 8px' }}>{v.component_name}</td><td style={{ padding: '4px 8px' }}>{v.goal_name}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Assigned Programs & Lead Status — unified with MyHomePM.js's own
// equivalent section via the shared ProgramCarousel (2026-08-31, condensed
// into a carousel per explicit ask + wireframe review — was a vertical stack
// of full ProgramStatusCards, each with its own click-to-expand-inline
// breakdown). Supplier-only extras — the per-status ⬇ download icon,
// ⬇ ZB Rejects, ⬇ Rejected-Lead Feedback — are passed to ProgramCarousel as
// per-item slot props; PM's own usage omits all three. This whole section
// can still collapse entirely (the outer Section wrapper) independent of
// which program tiles are expanded.
// ---------------------------------------------------------------------------
function AssignedProgramsStatus({
  supplierStatus, loading, onRefresh, programStatuses,
  onDownloadStatusLeads, onDownloadZbRejects, onDownloadRejectedFeedback, onDownloadTargetingList,
}) {
  const statusGroupMap = buildStatusGroupMap(programStatuses);
  const { active: activeProgs, closed: closedProgs } = splitByStatusGroup(
    supplierStatus || [], { statusKey: 'program_status', groupMap: statusGroupMap }
  );

  const buildItem = (prog) => {
    const includesCallReview = (prog.goals || []).some(g => g.includes_call_review);
    const acceptedCount = (prog.goals || []).reduce((s, g) => s + (g.lead_counts?.['Client Accepted'] || 0), 0);
    const goalCount = prog.leads_ordered != null
      ? prog.leads_ordered
      : (prog.goals || []).reduce((s, g) => s + (g.quantity_ordered || 0), 0);
    return {
      id: prog.program_id,
      program: {
        id: prog.program_id, program_name: prog.program_name, status: prog.program_status,
        flight_date_start: prog.flight_date_start, flight_date_end: prog.flight_date_end,
        accepted_count: acceptedCount, goal_count: goalCount || null,
      },
      rows: supplierGoalsToRows(prog.goals),
      includesCallReview,
      renderStatusHeader: (s) => (
        <th key={s} style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #ddd', color: LEAD_STATUS_COLORS[s]?.color, fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap', background: '#f5f5f5' }}>
          {s}
          <button
            onClick={() => onDownloadStatusLeads(prog.program_id, s)}
            title={`Download all "${s}" leads for this program`}
            style={{ marginLeft: '4px', border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6, fontSize: '11px', padding: 0, verticalAlign: 'middle' }}
          >
            ⬇
          </button>
        </th>
      ),
      headerActions: (
        <>
          <button
            onClick={() => onDownloadRejectedFeedback(prog.program_id)}
            style={{ padding: '3px 10px', background: 'white', border: '1px solid #3498db', color: '#1a6da8', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
            ⬇ Rejected-Lead Feedback
          </button>
          <button
            onClick={() => onDownloadZbRejects(prog.program_id)}
            title="Download every ZeroBounce-rejected email across every delivery for this program (cumulative)"
            style={{ padding: '3px 10px', background: 'white', border: '1px solid #95a5a6', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
            ⬇ ZB Rejects
          </button>
        </>
      ),
      // Nested in their own collapsed "Client Lists" sub-section (2026-09-02,
      // real ask — the header row got too busy once a program with several
      // account/suppression lists piled buttons on next to Rejected-Lead
      // Feedback/ZB Rejects) instead of headerActions. See ProgramCarousel.js's
      // clientLists prop.
      clientLists: [
        ...(prog.account_lists || []).map(l => (
          <button key={`al-${l.id}`}
            onClick={() => onDownloadTargetingList(l.id, l.list_name)}
            title={`Download the "${l.list_name}" account list`}
            style={{ padding: '3px 10px', background: 'white', border: '1px solid #8e44ad', color: '#6c3483', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
            ⬇ Account List — {l.list_name}
          </button>
        )),
        ...(prog.suppression_lists || []).map(l => (
          <button key={`sup-${l.id}`}
            onClick={() => onDownloadTargetingList(l.id, l.list_name)}
            title={`Download the "${l.list_name}" suppression list${l.is_global ? ' (applies company-wide)' : ''}`}
            style={{ padding: '3px 10px', background: 'white', border: '1px solid #e67e22', color: '#a04000', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
            ⬇ Suppression List — {l.list_name}
          </button>
        )),
      ],
    };
  };

  return (
    <Section
      id="assigned-programs-status" title="Assigned Programs & Lead Status"
      count={supplierStatus?.length || 0}
      extra={onRefresh && (
        <button onClick={onRefresh}
          title="Reload current status — these counts don't auto-update after an accept/QA action taken elsewhere"
          style={{ padding: '3px 10px', background: 'white', border: '1px solid #95a5a6', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
          🔄 Refresh
        </button>
      )}
    >
      {loading ? (
        <div style={{ color: '#aaa', fontSize: '13px' }}>Loading…</div>
      ) : !supplierStatus?.length ? (
        <div style={{ color: '#aaa', fontSize: '13px', textAlign: 'center', padding: '12px' }}>No active programs found.</div>
      ) : (
        <ProgramCarousel
          items={activeProgs.map(buildItem)}
          closedItems={closedProgs.map(buildItem)}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Section C: Delivery History
// ---------------------------------------------------------------------------
function DeliveryHistory({ myPrograms, downloadFile, refreshSignal, programStatuses }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [filterProgram, setFilterProgram] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (days) params.set('days', days);
      if (filterProgram) params.set('program_id', filterProgram);
      const res = await axios.get(`${API}/supplier-deliveries/?${params}`);
      setDeliveries(res.data);
    } catch {
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, [days, filterProgram]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const inputStyle = { padding: '6px 10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', background: 'white', cursor: 'pointer' };

  // GET /supplier-deliveries/ doesn't carry program_name or status — resolve both
  // from myPrograms (already fetched, has both). Deliveries for a Closed program
  // nest into their own collapsed section at the bottom, same status_group
  // classification used by Current Status and the Upload picker above; a plain
  // partition preserving the backend's existing upload-date-desc order, not a
  // re-sort.
  const statusGroupMap = buildStatusGroupMap(programStatuses);
  // Closed programs nest into a collapsible section on the Program filter
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const programOptionGroups = buildProgramOptionGroups(myPrograms, statusGroupMap);
  const activeDeliveries = [];
  const closedDeliveries = [];
  deliveries.forEach(raw => {
    const prog = myPrograms.find(p => p.id === raw.program_id);
    const d = { ...raw, program_name: raw.program_name || prog?.program_name };
    const group = statusGroupMap[prog?.status];
    (group === 'closed' ? closedDeliveries : activeDeliveries).push(d);
  });

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={days} onChange={e => setDays(Number(e.target.value))} style={inputStyle}>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last 365 days</option>
          <option value={0}>All time</option>
        </select>
        <SearchableSelect value={filterProgram} onChange={setFilterProgram}
          placeholder="All Programs"
          options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions} />
      </div>

      {loading ? (
        <div style={{ color: '#aaa', fontSize: '13px' }}>Loading…</div>
      ) : deliveries.length === 0 ? (
        <div style={{ padding: '24px', background: 'white', border: '1px solid #eee', borderRadius: '8px', color: '#aaa', textAlign: 'center', fontSize: '13px' }}>
          No deliveries found for the selected time range and filter.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {activeDeliveries.map(d => <DeliveryCard key={d.id} d={d} onDownload={downloadFile} onAccepted={load} />)}
          </div>
          {closedDeliveries.length > 0 && (
            <CollapsibleSection label="Closed programs" count={closedDeliveries.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {closedDeliveries.map(d => <DeliveryCard key={d.id} d={d} onDownload={downloadFile} onAccepted={load} />)}
              </div>
            </CollapsibleSection>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin panel
// ---------------------------------------------------------------------------
function AdminDeliveriesPanel() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [rerunning, setRerunning] = useState({});
  const [rerunAllBusy, setRerunAllBusy] = useState(false);
  const [rerunAllResult, setRerunAllResult] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/supplier-deliveries/admin/all`)
      .then(r => setDeliveries(r.data)).catch(() => setDeliveries([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this delivery? This cannot be undone.')) return;
    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      await axios.delete(`${API}/supplier-deliveries/${id}`);
      setDeliveries(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleRerunZb = async (id) => {
    setRerunning(prev => ({ ...prev, [id]: true }));
    try {
      await axios.post(`${API}/supplier-deliveries/${id}/rerun-zb`);
      await load(); // refresh to show updated ZB results
    } catch (err) {
      alert('Re-run failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRerunning(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleRerunAll = async () => {
    if (!window.confirm('Re-run ZeroBounce on ALL deliveries? This will use ZeroBounce credits for every delivery with file content.')) return;
    setRerunAllBusy(true);
    setRerunAllResult(null);
    try {
      const res = await axios.post(`${API}/supplier-deliveries/admin/rerun-all-zb`);
      setRerunAllResult(res.data);
      await load();
    } catch (err) {
      alert('Bulk re-run failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRerunAllBusy(false);
    }
  };

  const handleBackfillAccept = async () => {
    if (!window.confirm('Repair deliveries marked "Delivered to PM" that still have leads stuck in Pending Review? Safe to run — only touches leads that were never actually accepted.')) return;
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`${API}/supplier-deliveries/admin/backfill-accept-stuck`);
      setBackfillResult(res.data);
      await load();
    } catch (err) {
      alert('Backfill failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBackfillBusy(false);
    }
  };

  const downloadFile = (id, filename, original = false) => {
    const url = `${API}/supplier-deliveries/${id}/file${original ? '?original=true' : ''}`;
    axios.get(url, { responseType: 'blob' })
      .then(res => { const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = filename || `delivery_${id}`; a.click(); URL.revokeObjectURL(a.href); })
      .catch(() => alert('Download failed'));
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '15px', color: '#2c3e50' }}>
          All Deliveries (Admin)
          {deliveries.length > 0 && <span style={{ marginLeft: '8px', background: '#95a5a6', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '12px' }}>{deliveries.length}</span>}
        </h3>
        <button onClick={handleBackfillAccept} disabled={backfillBusy}
          title='Repair delivered batches whose leads never actually got flipped out of Pending Review (e.g. delivered before Accept was wired in)'
          style={{ marginLeft: 'auto', padding: '5px 12px', background: backfillBusy ? '#ccc' : '#16a085', color: 'white', border: 'none', borderRadius: '4px', cursor: backfillBusy ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
          {backfillBusy ? '🔧 Running…' : '🔧 Fix Stuck Deliveries'}
        </button>
        <button onClick={handleRerunAll} disabled={rerunAllBusy}
          style={{ padding: '5px 12px', background: rerunAllBusy ? '#ccc' : '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', cursor: rerunAllBusy ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
          {rerunAllBusy ? '🔄 Running…' : '🔄 Re-run ZB on All'}
        </button>
      </div>

      {backfillResult && (
        <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#eafaf1', border: '1px solid #27ae60', borderRadius: '6px', fontSize: '12px', color: '#1e8449' }}>
          ✓ Repaired {backfillResult.processed} stuck deliver{backfillResult.processed === 1 ? 'y' : 'ies'}
          {backfillResult.processed > 0 && <span> — {backfillResult.results.reduce((s, r) => s + r.updated, 0)} lead(s) moved to Under Review.</span>}
        </div>
      )}

      {rerunAllResult && (
        <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#eafaf1', border: '1px solid #27ae60', borderRadius: '6px', fontSize: '12px', color: '#1e8449' }}>
          ✓ Processed {rerunAllResult.processed} deliveries.{' '}
          {rerunAllResult.results?.filter(r => r.status === 'error').length > 0 &&
            <span style={{ color: '#e74c3c' }}>{rerunAllResult.results.filter(r => r.status === 'error').length} errors.</span>}
        </div>
      )}

      {loading ? <div style={{ color: '#aaa', fontSize: '13px' }}>Loading…</div>
      : deliveries.length === 0 ? <div style={{ color: '#aaa', fontSize: '13px', padding: '20px', background: 'white', border: '1px solid #eee', borderRadius: '8px', textAlign: 'center' }}>No deliveries uploaded yet.</div>
      : <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {deliveries.map(d => (
            <div key={d.id}>
              <DeliveryCard d={d} onDownload={downloadFile} onDelete={handleDelete} deleting={deleting} onAccepted={load} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button onClick={() => handleRerunZb(d.id)} disabled={rerunning[d.id]}
                  style={{ padding: '3px 10px', background: rerunning[d.id] ? '#ccc' : 'white', border: '1px solid #8e44ad', color: rerunning[d.id] ? '#aaa' : '#8e44ad', borderRadius: '4px', cursor: rerunning[d.id] ? 'not-allowed' : 'pointer', fontSize: '11px' }}>
                  {rerunning[d.id] ? '🔄 Running…' : '🔄 Re-run ZeroBounce'}
                </button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SupplierPortal() {
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role !== 'partner';
  const fileRef = useRef();

  const [goals, setGoals] = useState([]);
  const [components, setComponents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programStatuses, setProgramStatuses] = useState([]);
  const [supplierStatus, setSupplierStatus] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);

  const [selectedProgramId, setSelectedProgramId] = useState(null);
  // Deep-link support: My Home's task CTAs open this page with ?program_id=
  // in the URL (window.open() can't carry React Router's in-memory `state`
  // across a new tab — see TargetingLists.js's selectListId for the same
  // dual-support pattern with a same-tab navigate() caller). Consumed once
  // the programs list has actually loaded, below.
  const location = useLocation();
  const programIdParam = new URLSearchParams(location.search).get('program_id');
  const pendingProgramId = useRef(
    location.state?.program_id || (programIdParam ? Number(programIdParam) : null)
  );
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // Live progress while the background upload job (ZeroBounce + ingestion)
  // runs — {status, stage, processed, total}. null when nothing is in flight.
  const [uploadJob, setUploadJob] = useState(null);
  const uploadPollRef = useRef(null);
  // Supplier's Yes/No answer to "not all leads passed, fix and resubmit?" —
  // null = not yet answered (prompt still showing), 'held' = chose Yes,
  // 'released' = chose No. Reset alongside uploadResult on every new upload.
  const [holdChoice, setHoldChoice] = useState(null);
  const [holdBusy, setHoldBusy] = useState(false);
  // Only fetched (and only ever shown) when a completed upload actually has
  // rejects — {has_suggestions, suggestions: [{field_key, field_label, tier,
  // unmapped_header, confidence}]}. This is a "did you mean" assist, never a
  // forced step: most uploads never trigger this fetch at all.
  const [mappingSuggestions, setMappingSuggestions] = useState(null);
  const [showMappingReview, setShowMappingReview] = useState(false);
  const [mappingChecked, setMappingChecked] = useState({}); // field_key -> bool, which suggestions are confirmed
  const [applyingMapping, setApplyingMapping] = useState(false);
  const [mappingApplyResult, setMappingApplyResult] = useState(null); // {message} once applied
  const [requiredFieldsSummary, setRequiredFieldsSummary] = useState(null);

  // ZeroBounce toggle state
  const [zbEnabled, setZbEnabled] = useState(true);
  const [zbEmailCol, setZbEmailCol] = useState('email');
  const [historyRefresh, setHistoryRefresh] = useState(0);

  // "Your Tasks" — GET /home/supplier, partner-only (admin has no personal
  // task list, this endpoint 403s for any non-partner role). Folded in from
  // the now-retired MyHomeSupplier.js (2026-08-31) — see App.js/MyHome.js.
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const loadTasks = useCallback(() => {
    if (isAdmin) { setTasksLoading(false); return; }
    setTasksLoading(true);
    axios.get(`${API}/home/supplier`)
      .then(r => setTasks(r.data?.tasks || []))
      .catch(() => setTasks([]))
      .finally(() => setTasksLoading(false));
  }, [isAdmin]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Current Status counts don't change on their own — a delivery being
  // accepted or a lead's QA status being promoted (both actionable from other
  // pages/tabs, or from a different section of this same page) never
  // triggers a refetch here otherwise. Exposed as "🔄 Refresh" in
  // AssignedProgramsStatus.
  const loadSupplierStatus = useCallback(() => {
    setStatusLoading(true);
    axios.get(`${API}/supplier-deliveries/status`)
      .then(r => setSupplierStatus(r.data))
      .catch(() => setSupplierStatus([]))
      .finally(() => setStatusLoading(false));
  }, []);

  useEffect(() => {
    axios.get(`${API}/goals/`).then(r => setGoals(r.data)).catch(() => {});
    axios.get(`${API}/components/`).then(r => setComponents(r.data)).catch(() => {});
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
    loadSupplierStatus();
  }, [loadSupplierStatus]);

  // Deep-link: select the requested program once it's actually in the
  // fetched list (see pendingProgramId above).
  useEffect(() => {
    if (pendingProgramId.current && programs.some(p => p.id === pendingProgramId.current)) {
      setSelectedProgramId(pendingProgramId.current);
      pendingProgramId.current = null;
    }
  }, [programs]);

  useEffect(() => {
    if (!selectedProgramId) { setRequiredFieldsSummary(null); return; }
    axios.get(`${API}/qa-rules/program/${selectedProgramId}/required-fields-summary`)
      .then(r => setRequiredFieldsSummary(r.data))
      .catch(() => setRequiredFieldsSummary(null));
  }, [selectedProgramId]);

  const myGoals = isAdmin ? goals : goals.filter(g => g.supplier_id === currentUser?.supplier_id);
  const myProgramIds = new Set(myGoals.map(g => components.find(c => c.id === g.component_id)?.program_id).filter(Boolean));
  const myPrograms = programs.filter(p => isAdmin || myProgramIds.has(p.id));
  const selectedProgramName = programs.find(p => p.id === selectedProgramId)?.program_name || '';

  // Open programs (Pending/Running) always visible, Closed nested in a collapsed
  // section — same lookup_values.status_group classification used by Current
  // Status above and the Programs/Components/Goals list pages.
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const { active: activeMyPrograms, closed: closedMyPrograms } = splitByStatusGroup(
    myPrograms, { statusKey: 'status', groupMap: programStatusGroupMap, dateKey: 'flight_date_end' }
  );

  const ProgramPickerButton = ({ p }) => (
    <button key={p.id} onClick={() => handleSelectProgram(p.id)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px', borderRadius: '6px', cursor: 'pointer', border: selectedProgramId === p.id ? '2px solid #3498db' : '1px solid #ddd', background: selectedProgramId === p.id ? '#eaf4fb' : 'white', color: selectedProgramId === p.id ? '#1a6da8' : '#2c3e50', fontWeight: selectedProgramId === p.id ? '600' : '400' }}>
      {p.program_name}
      {p.status && (
        <span style={{ background: PROGRAM_STATUS_COLORS[p.status] || '#95a5a6', color: 'white', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: '600' }}>
          {p.status}
        </span>
      )}
    </button>
  );

  const programComponents = components.filter(c => c.program_id === selectedProgramId);
  const goalsForProgram = myGoals.filter(g => components.find(c => c.id === g.component_id)?.program_id === selectedProgramId);
  const goalsByComponent = programComponents.map(comp => ({
    comp, goals: goalsForProgram.filter(g => g.component_id === comp.id),
  })).filter(group => group.goals.length > 0);

  const handleSelectProgram = (programId) => {
    setSelectedProgramId(programId);
    setUploadFile(null);
    setUploadResult(null);
  };

  // ── Your Tasks handlers (ported from the now-retired MyHomeSupplier.js,
  // 2026-08-31) — see src/hooks/useTaskActions.js. Was a hand-copied
  // implementation shared verbatim with MyHomePM.js until 2026-09-01;
  // `tasks` here is its own top-level state (not nested, unlike MyHomePM's
  // combined payload), so the updater is just `setTasks` directly.
  const { handleToggleCompleted, handleSnooze, handleDismiss } = useTaskActions(setTasks, loadTasks);
  // Now that Your Tasks lives on the same page as everywhere it used to
  // deep-link to, a CTA click selects the relevant program in place and
  // scrolls to Assigned Programs & Lead Status, instead of opening a
  // redundant new tab of this same page (the old MyHomeSupplier.js
  // behavior, back when the task list and the destination were two
  // different pages). The one exception is the weekly "Add expected
  // deliveries" reminder — it has no program of its own to select, so it
  // just scrolls to the This-week calendar, where the "+ Add" control lives.
  const handleTaskCta = (task) => {
    if (task.source === 'reminder') {
      document.getElementById('this-week-tasks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (task.program_id) {
      handleSelectProgram(task.program_id);
      document.getElementById('assigned-programs-status')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Supplier Portal's own write path onto Week at a Glance — see backend
  // CLAUDE.md's week_board router section (POST /week-board/supplier-items).
  // Called from the This-week calendar's per-day "+ Add" control now, not a
  // per-program-card form (2026-08-31 — one place to log an expected
  // delivery instead of two).
  const handleAddExpectedDelivery = async (programId, activityDate, note, expectedCount) => {
    try {
      await axios.post(`${API}/week-board/supplier-items`, {
        program_id: programId, activity_date: activityDate, note, expected_count: expectedCount,
      });
      await Promise.all([loadTasks(), loadSupplierStatus()]);
    } catch (err) {
      alert('Failed to add: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Self-serve download of this supplier's own currently-rejected leads on
  // a program — replaces a PM having to run and hand over an export.
  const handleDownloadRejectedFeedback = (programId) => {
    axios.get(`${API}/supplier-deliveries/rejected-feedback/${programId}`, { responseType: 'blob' })
      .then(res => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = `rejected_feedback_program${programId}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => {
        if (err.response?.status === 404) alert('No rejected leads to download for this program right now.');
        else alert('Download failed.');
      });
  };

  // Downloads every lead in this program currently at the given status —
  // across all components/goals, every field that has a value (program_id/
  // component_id/batch_id/lead_id/qa_flags included) — see GET
  // /supplier-deliveries/status/{program_id}/export. Passed to
  // AssignedProgramsStatus as onDownloadStatusLeads.
  const handleDownloadStatusLeads = (programId, status) => {
    axios.get(`${API}/supplier-deliveries/status/${programId}/export`, {
      params: { status }, responseType: 'blob',
    }).then(res => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `leads_program${programId}_${status.replace(/ /g, '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(() => alert('Download failed'));
  };

  // Downloads a targeting list's items as XLSX — the per-program "Account
  // List" / "Suppression List" links in Assigned Programs & Lead Status
  // (2026-09-02, real ask: a supplier wants to see exactly which accounts
  // they're targeting, or which domains/emails are excluded, without
  // asking a PM). Shares GET /targeting-lists/{id}/items/download with the
  // admin/PM-facing List Management page — scoped server-side to lists
  // actually assigned to one of this supplier's own programs (or global
  // ones), see targeting_lists.py's _partner_can_access_list(). Passed to
  // AssignedProgramsStatus as onDownloadTargetingList.
  const handleDownloadTargetingList = (listId, listName) => {
    axios.get(`${API}/targeting-lists/${listId}/items/download`, { responseType: 'blob' })
      .then(res => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = `${(listName || `list_${listId}`).replace(/[\s/]+/g, '_')}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Download failed'));
  };

  // Cumulative ZeroBounce rejects for this program — every rejected row
  // across every delivery ever uploaded, not just one delivery's own "Batch
  // Feedback" download. See GET /supplier-deliveries/status/{program_id}/
  // zb-rejects-export. Passed to AssignedProgramsStatus as onDownloadZbRejects.
  const handleDownloadZbRejects = (programId) => {
    axios.get(`${API}/supplier-deliveries/status/${programId}/zb-rejects-export`, {
      responseType: 'blob',
    }).then(res => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `zb_rejects_program${programId}_cumulative.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(() => alert('Download failed'));
  };

  // Runs in the background on the server (ZeroBounce verification can be up
  // to 1000 sequential API calls, then lead ingestion its own per-row DB
  // work) — POST /upload returns a job id immediately instead of holding the
  // connection open for however long that takes, which was long enough on a
  // real-sized file to trip a proxy/load-balancer timeout and show a false
  // "Upload failed" even though it actually succeeded server-side. Poll here
  // until the job finishes, then reshape its result into the exact same
  // `{success, data}` shape this page's rendering below already expects, so
  // nothing past this function needed to change.
  const pollUploadJob = useCallback((jobId) => {
    if (uploadPollRef.current) clearInterval(uploadPollRef.current);
    uploadPollRef.current = setInterval(async () => {
      try {
        const { data: job } = await axios.get(`${API}/supplier-deliveries/upload-jobs/${jobId}`);
        setUploadJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(uploadPollRef.current);
          uploadPollRef.current = null;
          setUploading(false);
          setUploadJob(null);
          if (job.status === 'completed') {
            setUploadResult({
              success: true,
              data: {
                ...job.delivery,
                zb_warning: job.zb_warning,
                leads_created: job.leads_created,
                leads_updated: job.leads_updated,
                leads_rejected: job.leads_rejected,
                leads_pending: job.leads_pending,
                ingestion_warning: job.ingestion_warning,
              },
            });
            setUploadFile(null);
            if (fileRef.current) fileRef.current.value = '';
            setHistoryRefresh(n => n + 1); // auto-refresh history
            // Always check, cheaply — the backend reads the file's own header
            // row directly, so it's correct even when every row was rejected
            // by ZeroBounce before ever reaching a required-field check
            // (leads_rejected alone would miss that case entirely: it only
            // counts leads that got far enough to be QA-rejected). The
            // "review mapping" banner only ever renders when has_suggestions
            // comes back true, so a clean upload still shows nothing.
            if (job.delivery?.id) {
              axios.get(`${API}/supplier-deliveries/${job.delivery.id}/mapping-suggestions`)
                .then(r => setMappingSuggestions(r.data))
                .catch(() => {});
            }
          } else {
            setUploadResult({ success: false, error: { message: job.error || 'Upload failed' } });
          }
        }
      } catch (err) {
        // A transient network hiccup on one poll shouldn't kill the whole
        // upload — just try again on the next tick. Only give up once the
        // interval itself is torn down (unmount/new upload).
      }
    }, 1500);
  }, [fileRef]);

  useEffect(() => () => { if (uploadPollRef.current) clearInterval(uploadPollRef.current); }, []);

  const handleUpload = async () => {
    if (!uploadFile || !selectedProgramId) return;
    setUploading(true);
    setUploadResult(null);
    setUploadJob(null);
    setHoldChoice(null);
    setMappingSuggestions(null);
    setShowMappingReview(false);
    setMappingChecked({});
    setMappingApplyResult(null);
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('program_id', selectedProgramId);
    if (zbEnabled && zbEmailCol.trim()) fd.append('email_column', zbEmailCol.trim());
    try {
      const res = await axios.post(`${API}/supplier-deliveries/upload`, fd);
      pollUploadJob(res.data.job_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setUploadResult({ success: false, error: typeof detail === 'object' ? detail : { message: detail || 'Upload failed' } });
      setUploading(false);
    }
  };

  // Not all leads passed -> supplier picks: hold this batch back from the PM
  // (fix + resubmit) or send it through as-is. Purely informational on the
  // backend (see hold-for-resubmission endpoint) — never blocks a PM who
  // finds the delivery another way.
  const handleHoldChoice = async (held) => {
    if (!uploadResult?.data?.id) return;
    if (!held) { setHoldChoice('released'); return; }
    setHoldBusy(true);
    try {
      await axios.patch(`${API}/supplier-deliveries/${uploadResult.data.id}/hold-for-resubmission`, { held: true });
      setHoldChoice('held');
      setHistoryRefresh(n => n + 1);
    } catch {
      alert('Could not save your choice — please try again.');
    } finally {
      setHoldBusy(false);
    }
  };

  // Applies every checked suggestion as a field_alias, one call per field —
  // this endpoint deliberately only ever touches one {field_key: alias} pair
  // at a time (see backend apply_field_alias_for_goal()'s docstring for why),
  // so confirming several suggestions at once is just several small calls,
  // not a bigger combined write.
  const handleApplyMapping = async () => {
    if (!uploadResult?.data?.id || !mappingSuggestions?.suggestions) return;
    const toApply = mappingSuggestions.suggestions.filter(s => mappingChecked[s.field_key]);
    if (toApply.length === 0) return;
    setApplyingMapping(true);
    try {
      const results = await Promise.all(toApply.map(s =>
        axios.post(`${API}/supplier-deliveries/${uploadResult.data.id}/apply-mapping-suggestion`, {
          field_key: s.field_key, alias: s.unmapped_header,
        }).then(r => r.data).catch(() => ({ applied: false }))
      ));
      const anyApplied = results.some(r => r.applied);
      const anyBlocked = results.some(r => r.goals_blocked?.length);
      setMappingApplyResult({
        success: anyApplied,
        message: anyApplied
          ? (anyBlocked
              ? 'Mapping saved for most fields — a couple need your PM to update instead. Re-upload your original file to reprocess the rejected leads.'
              : 'Mapping saved. Re-upload your original file to reprocess the leads that were rejected for this.')
          : (anyBlocked
              ? "This program's required fields setup needs a PM to update before self-serve mapping fixes can apply here — contact your PM."
              : 'Could not save the mapping — please try again.'),
      });
      setShowMappingReview(false);
    } catch {
      setMappingApplyResult({ success: false, message: 'Could not save the mapping — please try again.' });
    } finally {
      setApplyingMapping(false);
    }
  };

  const downloadBatchFeedback = (deliveryId) => {
    axios.get(`${API}/supplier-deliveries/${deliveryId}/export-batch-feedback`, { responseType: 'blob' })
      .then(res => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = `delivery_${deliveryId}_batch_feedback.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Download failed'));
  };

  const downloadFile = (deliveryId, filename) => {
    axios.get(`${API}/supplier-deliveries/${deliveryId}/file`, { responseType: 'blob' })
      .then(res => { const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = filename || `delivery_${deliveryId}`; a.click(); URL.revokeObjectURL(a.href); })
      .catch(() => alert('Download failed'));
  };

  // Builds a CSV template (header row + one blank row) from the program's
  // resolved Required Fields — Program ID/Component ID/Goal ID first, then
  // every tracked field across all component groups (deduped by key; a field
  // appearing at different tiers in different groups keeps its strictest
  // tier), ordered Required (reject) -> Requested (review) -> Optional. Built
  // entirely client-side from data the panel already fetched, same pattern as
  // LeadDeliverySetup.js's downloadLeadTemplate(). When a field has a
  // client_alias (QA Rules' Required Fields section), the header shows both
  // names — "{NeTRA label} ({client's name})" — same "alongside" treatment
  // as RequiredFieldsSummary's inline display above. Still round-trips on
  // upload: the backend strips a trailing "(...)" clarifier before matching
  // a header, falling back to the plain label's own match (see leads.py's
  // _extract_contact_fields).
  const TIER_ORDER = { reject: 0, review: 1, optional: 2 };
  const downloadFieldTemplate = (groups, programId, programName) => {
    if (!groups || groups.length === 0) return;
    const fieldMap = new Map();
    groups.forEach(g => {
      g.fields.forEach(f => {
        const existing = fieldMap.get(f.key);
        if (!existing || (TIER_ORDER[f.tier] ?? 3) < (TIER_ORDER[existing.tier] ?? 3)) {
          const label = f.client_alias ? `${f.label} (${f.client_alias})` : f.label;
          fieldMap.set(f.key, { label, tier: f.tier });
        }
      });
    });
    const sortedFields = Array.from(fieldMap.values())
      .sort((a, b) => (TIER_ORDER[a.tier] ?? 3) - (TIER_ORDER[b.tier] ?? 3));
    const headers = ['Program ID', 'Component ID', 'Goal ID', ...sortedFields.map(f => f.label)];
    const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const exampleRow = headers.map(h => (h === 'Program ID' ? String(programId) : ''));
    const csv = [headers.map(esc).join(','), exampleRow.map(esc).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(programName || 'program').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_upload_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Programs a supplier can log an expected delivery against, for the
  // This-week calendar's "+ Add" control — active (non-closed) programs
  // only, same set the Upload picker already offers.
  const addDeliveryProgramOptions = activeMyPrograms.map(p => ({ value: p.id, label: p.program_name }));

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '18px' }}>
        <h2 style={{ margin: '0 0 4px', color: '#2c3e50' }}>{isAdmin ? 'Supplier Deliveries' : 'Supplier Portal'}</h2>
        <p style={{ margin: 0, color: '#7f8c8d', fontSize: '13px' }}>
          {isAdmin ? 'View and manage delivery files uploaded by suppliers.' : 'Your tasks, lead status, uploads, and delivery history — all in one place.'}
        </p>
      </div>

      {/* ── Section: Your Tasks (partners only) — merged in from the now-
          retired MyHomeSupplier.js, 2026-08-31. Open on page load. ── */}
      {!isAdmin && (
        <Section id="your-tasks" title="Your Tasks" count={tasks.length} defaultOpen={tasks.length > 0}>
          {tasksLoading ? (
            <div style={{ color: '#aaa', fontSize: '13px' }}>Loading…</div>
          ) : (
            <TaskList
              tasks={tasks}
              onToggleCompleted={handleToggleCompleted}
              onSnooze={handleSnooze}
              onDismiss={handleDismiss}
              onCta={handleTaskCta}
              defaultThisWeekView="calendar"
              addDeliveryPrograms={addDeliveryProgramOptions}
              onAddDelivery={handleAddExpectedDelivery}
            />
          )}
          <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => window.open('/week-at-a-glance', '_blank')}
              style={{ flexShrink: 0, padding: '7px 14px', background: 'white', border: '1px solid #ddd', color: '#2c3e50', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontWeight: '600', whiteSpace: 'nowrap' }}>
              📅 Week at a Glance
            </button>
          </div>
        </Section>
      )}

      {/* ── Section: Assigned Programs & Lead Status (partners only) ── */}
      {!isAdmin && (
        <AssignedProgramsStatus
          supplierStatus={supplierStatus}
          loading={statusLoading}
          onRefresh={loadSupplierStatus}
          programStatuses={programStatuses}
          onDownloadStatusLeads={handleDownloadStatusLeads}
          onDownloadZbRejects={handleDownloadZbRejects}
          onDownloadRejectedFeedback={handleDownloadRejectedFeedback}
          onDownloadTargetingList={handleDownloadTargetingList}
        />
      )}

      {/* ── Section: Upload a Delivery — collapsed on page load ── */}
      <Section id="upload-delivery" title="Upload a Delivery">
        {/* Program selection */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>
            {selectedProgramId ? `Program: ${selectedProgramName}` : 'Step 1 — Select a program'}
          </div>
          {myPrograms.length === 0 ? (
            <div style={{ padding: '16px', background: 'white', border: '1px solid #eee', borderRadius: '8px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>No programs available.</div>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {activeMyPrograms.map(p => <ProgramPickerButton key={p.id} p={p} />)}
              </div>
              {closedMyPrograms.length > 0 && (
                <CollapsibleSection label="Closed" count={closedMyPrograms.length}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {closedMyPrograms.map(p => <ProgramPickerButton key={p.id} p={p} />)}
                  </div>
                </CollapsibleSection>
              )}
            </>
          )}
        </div>

        {selectedProgramId && (
          <>
            {/* Valid IDs */}
            <div style={{ marginBottom: '16px', background: '#f0f7ff', border: '1px solid #aed6f1', borderRadius: '8px', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a6da8', marginBottom: '4px' }}>Step 2 — Valid IDs for <em>{selectedProgramName}</em></div>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                Your file must include <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>Program ID</code>,{' '}
                <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>Component ID</code>, and{' '}
                <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>Goal ID</code> columns. Click headers to sort.
              </div>
              {goalsByComponent.length === 0
                ? <div style={{ fontSize: '12px', color: '#aaa' }}>No goals assigned to you for this program.</div>
                : <ValidIdsTable goalsByComponent={goalsByComponent} selectedProgramId={selectedProgramId} />}
            </div>

            {/* Required fields for this program */}
            <div style={{ marginBottom: '16px' }}>
              {requiredFieldsSummary && requiredFieldsSummary.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                  <button
                    onClick={() => downloadFieldTemplate(requiredFieldsSummary, selectedProgramId, selectedProgramName)}
                    title="Downloads a CSV with a header row for every field tracked for this program (required, requested, and optional)."
                    style={{ padding: '5px 12px', background: 'white', border: '1px solid #3498db', color: '#1a6da8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                    ⬇ Download Upload Template
                  </button>
                </div>
              )}
              <RequiredFieldsSummary groups={requiredFieldsSummary} />
            </div>

            {/* Upload */}
            <div style={{ background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', padding: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '12px' }}>Step 3 — Upload file</div>
              <DropZone
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                setUploadResult={setUploadResult}
                fileRef={fileRef}
              />

              <ZbToggle
                enabled={zbEnabled}
                onToggle={setZbEnabled}
                emailCol={zbEmailCol}
                onColChange={setZbEmailCol}
              />

              <button onClick={handleUpload} disabled={!uploadFile || uploading}
                style={{ width: '100%', padding: '10px', fontSize: '14px', border: 'none', borderRadius: '6px', marginTop: '12px', cursor: uploadFile && !uploading ? 'pointer' : 'not-allowed', background: uploadFile && !uploading ? '#27ae60' : '#ccc', color: 'white', fontWeight: '600' }}>
                {uploading
                  ? (uploadJob?.stage === 'ingest'
                      ? `Creating leads… (${uploadJob.processed}/${uploadJob.total})`
                      : uploadJob?.stage === 'zb'
                        ? 'Verifying emails…'
                        : (zbEnabled ? 'Uploading & verifying emails…' : 'Uploading…'))
                  : '📤 Upload'}
              </button>
              {uploading && uploadJob?.stage === 'ingest' && uploadJob.total > 0 && (
                <div style={{ marginTop: '6px', height: '6px', borderRadius: '3px', background: '#eee', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '3px', background: '#27ae60',
                    width: `${Math.min(100, Math.round((uploadJob.processed / uploadJob.total) * 100))}%`,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              )}

              {uploadResult && (() => {
                const d = uploadResult.data;
                // leads_rejected only counts rows that became a Lead row and
                // THEN failed a QA check — a row ZeroBounce flags never
                // becomes a Lead row at all, so it was never eligible to be
                // counted there. That's correct for what leads_rejected
                // itself means, but "all leads passed" has to account for
                // BOTH gates, or a batch ZeroBounce rejected in full (e.g.
                // every address on a disposable-email domain) reads as a
                // clean success when literally nothing got through.
                const zbInvalid = d?.row_counts?.zb_invalid || 0;
                const totalRejectedAny = (d?.leads_rejected || 0) + zbInvalid;
                const allPassed = !isAdmin && !d?.ingestion_warning && totalRejectedAny === 0;
                const hasRejects = !isAdmin && !d?.ingestion_warning && totalRejectedAny > 0;
                // Only true when EVERY row was killed by ZeroBounce specifically
                // (none reached QA at all) — the wording needs to say that
                // plainly rather than implying a QA failure that never happened.
                const allZbRejected = hasRejects && zbInvalid > 0 && (d?.leads_rejected || 0) === 0
                  && zbInvalid >= (d?.row_counts?.total || 0);
                return (
                <div style={{ marginTop: '14px' }}>
                  {uploadResult.success ? (
                    <div style={{ padding: '14px', borderRadius: '6px', background: '#eafaf1', border: '1px solid #27ae60' }}>
                      <div style={{ color: allZbRejected ? '#b7770d' : '#1e8449', fontWeight: '600', fontSize: '13px', marginBottom: '10px' }}>
                        {allPassed
                          ? '✓ Well done! All leads passed!'
                          : allZbRejected
                            ? `⚠ All ${d.row_counts?.total || zbInvalid} lead${(d.row_counts?.total || zbInvalid) !== 1 ? 's were' : ' was'} rejected by email verification — none reached QA review.`
                            : `✓ Upload successful for ${selectedProgramName} — status: Pending`}
                      </div>
                      <RowBreakdown counts={d.row_counts} status="Pending" />
                      {d.zb_warning && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#b7770d', background: '#fef9e7', border: '1px solid #f39c12', borderRadius: '4px', padding: '6px 10px' }}>
                          ⚠ {d.zb_warning}
                        </div>
                      )}
                      {hasRejects && holdChoice === null && (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#fff8f0', border: '1px solid #f0c896', borderRadius: '4px' }}>
                          <div style={{ fontSize: '12px', color: '#8a5a1e', fontWeight: '600', marginBottom: '8px' }}>
                            Not all leads passed — would you like to fix any and resubmit this batch?
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => handleHoldChoice(true)} disabled={holdBusy}
                              style={{ padding: '5px 14px', background: '#e67e22', color: 'white', border: 'none', borderRadius: '4px', cursor: holdBusy ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600' }}>
                              Yes
                            </button>
                            <button onClick={() => handleHoldChoice(false)} disabled={holdBusy}
                              style={{ padding: '5px 14px', background: 'white', border: '1px solid #aaa', color: '#555', borderRadius: '4px', cursor: holdBusy ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                              No
                            </button>
                          </div>
                        </div>
                      )}
                      {holdChoice === 'held' && (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#eaf0fb', border: '1px solid #aed6f1', borderRadius: '4px' }}>
                          <div style={{ fontSize: '12px', color: '#1a6da8', marginBottom: '8px' }}>
                            This batch will not be sent to your PM yet. Download the batch feedback below, fix the flagged leads, and re-upload your corrected file — matching leads (by NeTRA Lead ID) will update automatically.
                          </div>
                          <button onClick={() => downloadBatchFeedback(d.id)}
                            style={{ padding: '5px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                            ⬇ Download Batch Feedback
                          </button>
                        </div>
                      )}
                      {holdChoice === 'released' && (
                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#1e8449' }}>
                          This batch will be reviewed by your PM.
                        </div>
                      )}
                      {uploadResult.data.leads_updated > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#1a6da8', background: '#eaf0fb', border: '1px solid #aed6f1', borderRadius: '4px', padding: '6px 10px' }}>
                          ✓ {uploadResult.data.leads_updated} lead{uploadResult.data.leads_updated !== 1 ? 's' : ''} updated in place (matched by NeTRA Lead ID).
                        </div>
                      )}
                      {mappingSuggestions?.has_suggestions && !mappingApplyResult && (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#fdf2f8', border: '1px solid #e8b4d8', borderRadius: '4px' }}>
                          <div style={{ fontSize: '12px', color: '#97266d', fontWeight: '600' }}>
                            Looks like you have some missing fields —{' '}
                            <button onClick={() => setShowMappingReview(s => !s)}
                              style={{ background: 'none', border: 'none', color: '#97266d', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px', fontWeight: '600', padding: 0 }}>
                              {showMappingReview ? 'hide mapping review' : 'review mapping'}
                            </button>
                          </div>
                          {showMappingReview && (
                            <div style={{ marginTop: '10px' }}>
                              <div style={{ fontSize: '11px', color: '#7a5169', marginBottom: '8px' }}>
                                These columns in your file might match fields we couldn't recognize. Confirm any that look right — we'll remember your spelling for future uploads.
                              </div>
                              {mappingSuggestions.suggestions.map(s => (
                                <label key={s.field_key} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#4a3040', marginBottom: '6px', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={!!mappingChecked[s.field_key]}
                                    onChange={e => setMappingChecked(c => ({ ...c, [s.field_key]: e.target.checked }))}
                                    style={{ marginTop: '2px' }} />
                                  <span>
                                    We expected <strong>{s.field_label}</strong> ({s.tier === 'reject' ? 'required' : s.tier === 'review' ? 'requested' : 'optional'}) — did you mean your column <strong>"{s.unmapped_header}"</strong>?
                                  </span>
                                </label>
                              ))}
                              <button onClick={handleApplyMapping}
                                disabled={applyingMapping || Object.values(mappingChecked).every(v => !v)}
                                style={{ marginTop: '6px', padding: '5px 14px', background: '#97266d', color: 'white', border: 'none', borderRadius: '4px', cursor: (applyingMapping || Object.values(mappingChecked).every(v => !v)) ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600' }}>
                                {applyingMapping ? 'Saving…' : 'Apply Mapping'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {mappingApplyResult && (
                        <div style={{
                          marginTop: '10px', fontSize: '12px', padding: '6px 10px', borderRadius: '4px',
                          color: mappingApplyResult.success ? '#1e8449' : '#c0392b',
                          background: mappingApplyResult.success ? '#eafaf1' : '#fdeded',
                          border: `1px solid ${mappingApplyResult.success ? '#a9dfbf' : '#f5b7b1'}`,
                        }}>
                          {mappingApplyResult.success ? '✓ ' : '⚠ '}{mappingApplyResult.message}
                        </div>
                      )}
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#7f8c8d' }}>
                        Your delivery now appears in the history below.
                      </div>
                    </div>
                  ) : (
                    <ValidationError error={uploadResult.error} />
                  )}
                </div>
                );
              })()}
            </div>
          </>
        )}
      </Section>

      {/* ── Section: Delivery History — collapsed on page load ── */}
      <Section id="delivery-history" title="Delivery History">
        <DeliveryHistory myPrograms={myPrograms} downloadFile={downloadFile} refreshSignal={historyRefresh} programStatuses={programStatuses} />
      </Section>

      {/* ── Admin panel (unchanged — not part of the 4-section unification) ── */}
      {isAdmin && (
        <section>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#7f8c8d', borderBottom: '2px solid #7f8c8d20', paddingBottom: '8px' }}>All Deliveries (Admin)</h3>
          <AdminDeliveriesPanel />
        </section>
      )}
    </div>
  );
}
